import "@/App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import MissionsPage from "./pages/MissionsPage";
import NewMissionPage from "./pages/NewMissionPage";
import MissionDetailPage from "./pages/MissionDetailPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import FleetPage from "./pages/FleetPage";
import NewVehiclePage from "./pages/NewVehiclePage";
import MyBidsPage from "./pages/MyBidsPage";
import SmartMatchPage from "./pages/SmartMatchPage";
import MessagesPage from "./pages/MessagesPage";
import TrackingPage from "./pages/TrackingPage";
import PricingPage from "./pages/PricingPage";
import CompliancePage from "./pages/CompliancePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import KYCPage from "./pages/KYCPage";
import StatusPage from "./pages/StatusPage";
import SupportPage from "./pages/SupportPage";
import AdminPage from "./pages/AdminPage";
import NotificationsPage from "./pages/NotificationsPage";
import AMLDashboardPage from "./pages/AMLDashboardPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/status" element={<StatusPage />} />

      {/* Protected Routes - All Users */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/missions/:id" element={<ProtectedRoute><MissionDetailPage /></ProtectedRoute>} />
      <Route path="/payments/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/kyc" element={<ProtectedRoute><KYCPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['enterprise']}><AdminPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />

      {/* Protected Routes - Enterprise Only */}
      <Route path="/missions" element={<ProtectedRoute allowedRoles={['enterprise']}><MissionsPage /></ProtectedRoute>} />
      <Route path="/missions/new" element={<ProtectedRoute allowedRoles={['enterprise']}><NewMissionPage /></ProtectedRoute>} />
      <Route path="/smartmatch" element={<ProtectedRoute allowedRoles={['enterprise']}><SmartMatchPage /></ProtectedRoute>} />
      <Route path="/tracking" element={<ProtectedRoute allowedRoles={['enterprise']}><TrackingPage /></ProtectedRoute>} />

      {/* Protected Routes - Carrier Only */}
      <Route path="/opportunities" element={<ProtectedRoute allowedRoles={['carrier']}><OpportunitiesPage /></ProtectedRoute>} />
      <Route path="/fleet" element={<ProtectedRoute allowedRoles={['carrier']}><FleetPage /></ProtectedRoute>} />
      <Route path="/fleet/new" element={<ProtectedRoute allowedRoles={['carrier']}><NewVehiclePage /></ProtectedRoute>} />
      <Route path="/my-bids" element={<ProtectedRoute allowedRoles={['carrier']}><MyBidsPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/aml" element={<ProtectedRoute allowedRoles={['enterprise']}><AMLDashboardPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute allowedRoles={['enterprise']}><AnalyticsDashboardPage /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
